import _ from 'lodash';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';

/**
 * In-memory store for runtime-only trend item values: state that is derived at runtime,
 * item-type independent, and must never be persisted. Keeping such values out of the item stores
 * means recomputing them can never dirty persisted state, so item stores can rely on the default
 * auto-push-on-change persistence without creating worksteps on every recompute.*
 */

export interface ItemYAxisExtremes {
  /** Raw minimum across the item's axis alignment group (or the user-set minimum) */
  yAxisMin: number | string;
  /** Raw maximum across the item's axis alignment group (or the user-set maximum) */
  yAxisMax: number | string;
  /** Buffered extremes actually applied to the chart axis */
  yAxisConfig: { min: number; max: number };
}

export interface ItemRuntimeState {
  /**
   * Computed y-axis extremes keyed by item id. Only parent items get entries; children render on
   * their parent's axis and fall back through `isChildOf` at read time.
   */
  yAxisExtremes: Record<string, ItemYAxisExtremes>;
  /**
   * ISO timestamp of the most recent data-fetch request keyed by item id. Used by plugins to recognize when a fetch
   * was requested and avoid redundant fetches.
   */
  lastFetchRequest: Record<string, string>;
  /**
   * Details-pane statistics (average, maximum, count, ...) keyed by item id. Computed from the
   * item's data over the display range (or selected region) whenever statistics columns are
   * enabled, and reset to an empty object when a new fetch begins.
   */
  statistics: Record<string, Record<string, unknown>>;
  /**
   * Per-request telemetry keyed by item id: fetch progress percentage plus the timing and
   * data-read information shown in the requests details panel
   */
  requestTelemetry: Record<string, ItemRequestTelemetry>;
  /** Data-request status keyed by item id; items with no entry have not started fetching */
  requestStatus: Record<string, ItemRequestStatus>;
}

export interface ItemRequestStatus {
  /** One of ITEM_DATA_STATUS */
  dataStatus: string;
  statusMessage: string;
  errorType?: string;
  errorCategory?: string;
  warningCount: number;
  warningLogs: unknown[];
  /** True when the item's timing and meter counts are totaled across multiple items */
  isSharedRequest?: boolean;
}

export interface ItemRequestTelemetry {
  /** Percent progress of the request */
  progress?: number | string;
  /** Timing information for the requests details panel */
  timingInformation?: string;
  /** Data-read (meter) information for the requests details panel */
  meterInformation?: string;
}

export interface ItemRuntimeActions {
  /** Replaces the whole map — used by the lane recompute, which covers every alignable item */
  setAllYAxisExtremes(yAxisExtremes: Record<string, ItemYAxisExtremes>): void;
  /** Merges entries for a subset of items — used by manual zoom and data-arrival updates */
  mergeYAxisExtremes(yAxisExtremes: Record<string, ItemYAxisExtremes>): void;
  setLastFetchRequest(id: string, timestamp: string): void;
  /** Replaces the item's statistics — callers always compute the full set for an item at once */
  setStatistics(id: string, statistics: Record<string, unknown>): void;
  /** Sets progress; sets timing/meter only when the payload carries those keys */
  setRequestProgress(
    id: string,
    payload: { progress?: number | string; timingInformation?: string; meterInformation?: string },
  ): void;
  /**
   * Replaces the item's request status and its timing/meter details (a payload without them
   * clears the stored values), preserving progress
   */
  setDataStatus(
    id: string,
    dataStatus: string,
    payload: {
      message?: string;
      warningCount?: number;
      /** A single log or a list; the results endpoints return either shape */
      warningLogs?: unknown;
      errorType?: string;
      errorCategory?: string;
      isSharedRequest?: boolean;
      timingInformation?: string;
      meterInformation?: string;
    },
  ): void;
  reset(): void;
}

export const initialState: ItemRuntimeState = Object.freeze({
  yAxisExtremes: {},
  lastFetchRequest: {},
  statistics: {},
  requestTelemetry: {},
  requestStatus: {},
});

const STORE_NAME = 'ItemRuntime';

const itemRuntimeActionsFactory: ActionsFactory<ItemRuntimeState, ItemRuntimeActions> = (set, get, resetState) => ({
  setAllYAxisExtremes: (yAxisExtremes) => {
    if (_.isEqual(get().yAxisExtremes, yAxisExtremes)) {
      return;
    }
    set((draft) => {
      draft.yAxisExtremes = yAxisExtremes;
    });
  },

  mergeYAxisExtremes: (yAxisExtremes) => {
    const current = get().yAxisExtremes;
    if (Object.entries(yAxisExtremes).every(([id, value]) => _.isEqual(current[id], value))) {
      return;
    }
    set((draft) => {
      Object.assign(draft.yAxisExtremes, yAxisExtremes);
    });
  },

  setLastFetchRequest: (id, timestamp) =>
    set((draft) => {
      draft.lastFetchRequest[id] = timestamp;
    }),

  setStatistics: (id, statistics) => {
    if (_.isEqual(get().statistics[id], statistics)) {
      return;
    }
    set((draft) => {
      draft.statistics[id] = statistics;
    });
  },

  setRequestProgress: (id, payload) =>
    set((draft) => {
      draft.requestTelemetry[id] ??= {};
      const entry = draft.requestTelemetry[id];
      entry.progress = payload.progress;
      if ('timingInformation' in payload) {
        entry.timingInformation = payload.timingInformation;
      }
      if ('meterInformation' in payload) {
        entry.meterInformation = payload.meterInformation;
      }
    }),

  setDataStatus: (id, dataStatus, payload) => {
    const status: ItemRequestStatus = {
      dataStatus,
      statusMessage: _.escape(payload.message),
      warningCount: _.isNumber(payload.warningCount) ? payload.warningCount : 0,
      warningLogs: _.isArray(payload.warningLogs)
        ? payload.warningLogs
        : _.isNil(payload.warningLogs)
          ? []
          : [payload.warningLogs],
      errorType: payload.errorType,
      errorCategory: payload.errorCategory,
      isSharedRequest: payload.isSharedRequest,
    };
    const current = get();
    const telemetry = current.requestTelemetry[id];
    if (
      _.isEqual(current.requestStatus[id], status) &&
      telemetry?.timingInformation === payload.timingInformation &&
      telemetry?.meterInformation === payload.meterInformation
    ) {
      return;
    }
    set((draft) => {
      draft.requestStatus[id] = status;
      draft.requestTelemetry[id] ??= {};
      draft.requestTelemetry[id].timingInformation = payload.timingInformation;
      draft.requestTelemetry[id].meterInformation = payload.meterInformation;
    });
  },

  reset: resetState,
});

const [ItemRuntimeStoreProvider, useItemRuntimeStoreInstance, useItemRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  itemRuntimeActionsFactory,
);

export { ItemRuntimeStoreProvider };

export const useYAxisExtremes = () => useItemRuntimeSelector((state) => state.yAxisExtremes);

export const useLastFetchRequests = () => useItemRuntimeSelector((state) => state.lastFetchRequest);

export const useItemStatistics = () => useItemRuntimeSelector((state) => state.statistics);

export const useRequestTelemetry = () => useItemRuntimeSelector((state) => state.requestTelemetry);

export const useRequestStatuses = () => useItemRuntimeSelector((state) => state.requestStatus);

export const useItemRuntimeActions = () => useItemRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateItemRuntimeStore(initialStateOverrides?: Partial<ItemRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    itemRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
